import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import Organization from '../organizations/organization-component';
import UserList from '../users/user-list-component';
import ExternalApplicationUserList from '../external-application/external-app-users/external-app-user-list-component';
import ExternalAppUserForm from '../external-application/external-app-users/external-app-user-form-component';
import './admin.scss';
import DictionaryTenantList from '../dictionary/dictionary-tenant-list-component';
import DictionaryHistory from '../dictionary/dictionary-history/dictionary-history-component';
import UserDetail from '../users/user-detail-component';
import DictionaryList from '../dictionary/dictionary-list-component';
import { Box, Grid } from '@chakra-ui/react';
import history from '../../history';
import { SecondaryNavigation, SecondaryNavigationOrientation, SecondaryNavigationVariants } from '../../components/secondary-navigation/secondary-navigation';
import { ORGANIZATION_CONSTANTS } from '../organizations/organization-constants';
import { styles } from './Admin-Styles';
import { useDispatch, useSelector } from 'react-redux';
import { navigate } from '../../actions/organization';
import DictionaryValueList from '../dictionary/dictionary-value-list/dictionary-value-list-component';
import { DictionaryUpload } from '../dictionary/dictionary-upload/dictionary-upload';
import RequestInProgress from '../dictionary/request-in-progress/request-in-progress.component';
import { DictionaryValueDetail } from '../dictionary/dictionary-value-detail';
import { KeyColumnSelection } from '../dictionary/column-metadata/key-column-select';
import { CrossMapSelect } from '../dictionary/column-metadata/cross-map-select';
import { CrossMapEdit } from '../dictionary/column-metadata/cross-map-edit';
import ExternalApplicationListV2 from '../external-application/external-application-list-component';
import AuditLog from '../audit-log/audit-log-component';
import { hasResource } from '../../core/resources';
import Groups from '../groups/groups-component';
import AddGroup from '../groups/groups-add-form-component';
import GroupsDetail from '../groups/groups-detail-component';
import Features from '../features/features-component';
import ConfigurationManagement from '../configuration-management/configuration-management';
export const Admin = (props)=>{
    const { navigateExternalApplication, navigateDictionaries, navigateGroups, navigateFeatures, navigateConfigurations } = props;
    // global states: redux
    const ui = useSelector((state)=>state.ui);
    const { context } = useSelector((state)=>state.ui);
    // local states: built-in react hooks
    const [activeTab, setActiveTab] = useState(0);
    const classes = styles();
    const location = useLocation();
    const isOrg = location?.pathname?.includes('orgs');
    const dispatch = useDispatch();
    // get enabled tabs (AdminTabs) from redux then determine default path
    const { AdminTabs } = ui;
    const defaultAdminTab = AdminTabs && AdminTabs.find((tab)=>tab.enabled);
    const defaultPath = defaultAdminTab ? `/cmx/${defaultAdminTab.path}` : '/cmx/admin/users/';
    const handleExternalAppClick = useCallback(()=>{
        navigateExternalApplication(props.activeOrganization.id);
    }, [
        navigateExternalApplication,
        props.activeOrganization.id
    ]);
    const handleDictionariesClick = useCallback(()=>{
        const activeOrg = props.activeOrganization;
        const tenantItem = {
            tenantName: activeOrg.organizationName,
            key: activeOrg.tenantId,
            id: activeOrg.id,
            organizationCode: activeOrg.organizationCode
        };
        navigateDictionaries(tenantItem);
    }, [
        navigateDictionaries,
        props.activeOrganization
    ]);
    const handleSubOrganizationsClick = useCallback(()=>{
        const organization = props.activeOrganization;
        const token = props.auth;
        if (organization?.tenantId) {
            history.replace(`/cmx/admin/orgs/${organization.id}/edit`);
            dispatch(navigate({
                token,
                organization
            }));
        }
    }, [
        dispatch,
        history,
        props.activeOrganization,
        props.auth
    ]);
    const handleGroupsClick = useCallback(()=>{
        navigateGroups(props.activeOrganization.id);
    }, [
        navigateGroups,
        props.activeOrganization.id
    ]);
    const handleFeaturesClick = useCallback(()=>{
        navigateFeatures('coder_config', props.activeOrganization.id);
    }, [
        navigateFeatures,
        props.activeOrganization.id
    ]);
    const handleConfigurationsClick = useCallback(()=>{
        navigateConfigurations(props.activeOrganization.id);
    }, [
        navigateConfigurations,
        props.activeOrganization.id
    ]);
    const subnavs = useMemo(()=>[
            {
                index: 0,
                label: ORGANIZATION_CONSTANTS.TENANTS,
                onClick: handleSubOrganizationsClick,
                matchPath: '/orgs'
            },
            {
                index: 1,
                label: ORGANIZATION_CONSTANTS.GROUPS,
                onClick: handleGroupsClick,
                matchPath: 'groups',
                disabled: !hasResource(context, ORGANIZATION_CONSTANTS.GROUPS.toLowerCase())
            },
            {
                index: 2,
                label: ORGANIZATION_CONSTANTS.FEATURES,
                onClick: handleFeaturesClick,
                matchPath: 'features'
            },
            {
                index: 3,
                label: ORGANIZATION_CONSTANTS.DICTIONARIES,
                onClick: handleDictionariesClick,
                matchPath: 'dictionaries'
            },
            {
                index: 4,
                label: ORGANIZATION_CONSTANTS.EXTERNAL_APPS,
                onClick: handleExternalAppClick,
                matchPath: 'external-applications'
            },
            {
                index: 5,
                label: ORGANIZATION_CONSTANTS.CONFIGURATIONS,
                onClick: handleConfigurationsClick,
                matchPath: 'configurations',
                disabled: !hasResource(context, 'view-configuration')
            }
        ], [
        context,
        handleConfigurationsClick,
        handleDictionariesClick,
        handleExternalAppClick,
        handleFeaturesClick,
        handleGroupsClick,
        handleSubOrganizationsClick
    ]);
    // react hook: whenever location changes, sync active tab with the current pathname
    useEffect(()=>{
        if (location?.pathname) {
            const nonTenantTab = subnavs.find((tab)=>tab.matchPath !== '/orgs' && location.pathname.includes(tab.matchPath));
            const tenantTab = subnavs.find((tab)=>tab.matchPath === '/orgs' && location.pathname.includes(tab.matchPath));
            // Check non-tenant tabs first since all tabs include '/orgs' in their path
            const matchedTab = nonTenantTab || tenantTab;
            if (matchedTab) {
                setActiveTab(matchedTab.index);
            }
        }
    }, [
        location,
        subnavs
    ]);
    const handleTabsChange = (e)=>{
        const tab = subnavs[e];
        if (tab && typeof tab.onClick === 'function' && !tab.disabled) {
            tab.onClick();
        } else {
            console.log(`Selected tabIndex ${e} is not present in organizations tabs constants`);
        }
    };
    return /*#__PURE__*/ _jsxs(Box, {
        sx: classes.root,
        children: [
            isOrg && /*#__PURE__*/ _jsx(Box, {
                sx: classes.tabs,
                children: /*#__PURE__*/ _jsx(SecondaryNavigation, {
                    data: subnavs,
                    variant: SecondaryNavigationVariants.UNSTYLED,
                    orientation: SecondaryNavigationOrientation.VERTICAL,
                    tabIndex: activeTab,
                    handleTabsChange: handleTabsChange
                })
            }),
            /*#__PURE__*/ _jsx(Grid, {
                sx: isOrg ? classes.content : classes.usersContent,
                children: /*#__PURE__*/ _jsxs(Switch, {
                    children: [
                        /*#__PURE__*/ _jsx(Route, {
                            exact: true,
                            path: "/cmx/admin/",
                            children: /*#__PURE__*/ _jsx(Redirect, {
                                to: defaultPath
                            })
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/",
                            exact: true,
                            component: Organization
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/(edit)",
                            exact: true,
                            component: Organization
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/groups",
                            exact: true,
                            component: Groups
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/groups/add",
                            exact: true,
                            component: AddGroup
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/groups/group/:id",
                            exact: true,
                            component: GroupsDetail
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/features",
                            exact: true,
                            component: Features
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/external-applications",
                            exact: true,
                            component: ExternalApplicationListV2
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/external-applications/:app",
                            exact: true,
                            component: ExternalApplicationUserList
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/external-applications/:app/users/add",
                            exact: true,
                            component: ExternalAppUserForm
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/external-applications/:app/users/edit",
                            exact: true,
                            component: ExternalAppUserForm
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/dictionaries/tenants",
                            exact: true,
                            component: DictionaryTenantList
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/dictionaries",
                            exact: true,
                            component: DictionaryList
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/dictionaries/dictionary/upload/new",
                            exact: true,
                            component: DictionaryUpload
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/dictionaries/dictionary/:id",
                            exact: true,
                            component: DictionaryValueList
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/dictionaries/dictionary/:id/metadata/keyColumn",
                            exact: true,
                            component: KeyColumnSelection
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/dictionaries/dictionary/:id/metadata/crossMaps",
                            exact: true,
                            render: (props)=>/*#__PURE__*/ _jsx(CrossMapSelect, {
                                    ...props,
                                    viewOnly: false
                                })
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/dictionaries/dictionary/:id/metadata/crossMaps/view_only",
                            exact: true,
                            render: (props)=>/*#__PURE__*/ _jsx(CrossMapSelect, {
                                    ...props,
                                    viewOnly: true
                                })
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/dictionaries/dictionary/:id/history",
                            exact: true,
                            render: ()=>/*#__PURE__*/ _jsx(DictionaryHistory, {})
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/dictionaries/dictionary/:id/metadata/crossMaps/:columnName/(edit)",
                            exact: true,
                            component: CrossMapEdit
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/dictionaries/dictionary/:id/upload",
                            exact: true,
                            component: DictionaryUpload
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/dictionaries/:requestIds",
                            exact: true,
                            component: RequestInProgress
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/dictionaries/dictionary/:id/row/:caseId/(edit)",
                            exact: true,
                            component: ()=>/*#__PURE__*/ _jsx(DictionaryValueDetail, {
                                    addRow: false
                                })
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/orgs/:id/dictionaries/dictionary/:id/row/add",
                            exact: true,
                            component: ()=>/*#__PURE__*/ _jsx(DictionaryValueDetail, {
                                    addRow: true
                                })
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            exact: true,
                            path: "/cmx/admin/orgs/:orgId/configurations",
                            render: (routeProps)=>/*#__PURE__*/ _jsx(ConfigurationManagement, {
                                    ...routeProps,
                                    tenantId: props.activeOrganization.tenantId,
                                    tenantName: props.activeOrganization.organizationName
                                })
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            exact: true,
                            path: "/cmx/admin/orgs/:orgId/configurations/:configId",
                            render: (routeProps)=>/*#__PURE__*/ _jsx(ConfigurationManagement, {
                                    ...routeProps,
                                    tenantId: props.activeOrganization.tenantId,
                                    tenantName: props.activeOrganization.organizationName
                                })
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/users/",
                            exact: true,
                            component: UserList
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/users/:id/",
                            exact: true,
                            component: UserDetail
                        }),
                        /*#__PURE__*/ _jsx(Route, {
                            path: "/cmx/admin/auditLog/",
                            exact: true,
                            component: AuditLog
                        })
                    ]
                })
            })
        ]
    });
};
export default Admin;
